import { TraceDetail } from "@admin/traces/TraceDetail";
import { Button } from "@scribenote/brian-design-system";
import { useState } from "react";

import type { ObservationIn, ObservationsReceipt } from "./perceptionClient";
import { clockWord } from "./words";

const TraceToggle = ({
  eventUuid,
  open,
  onToggle,
}: {
  eventUuid: string;
  open: boolean;
  onToggle: (eventUuid: string) => void;
}) => (
  <Button variant="ghost" size="sm" onClick={() => onToggle(eventUuid)}>
    {open ? "Hide trace" : "Trace"}
  </Button>
);

/**
 * What brian recorded for one submit: each observation's event, and the one
 * `room.occupants_changed` when the roster moved. Traces open inline because
 * Settings > Traces has no route to a single event; `/event-traces` is
 * leadership only, so others see the event without the trace.
 */
export const ObservationReceipt = ({
  receipt,
  sent,
  leadership,
}: {
  receipt: ObservationsReceipt;
  sent: readonly ObservationIn[];
  leadership: boolean;
}) => {
  const [trace, setTrace] = useState<string | null>(null);
  const toggle = (eventUuid: string) =>
    setTrace((open) => (open === eventUuid ? null : eventUuid));
  const applied = receipt.results.filter((r) => r.status === "applied").length;
  const duplicate = receipt.results.length - applied;
  const sentByUuid = new Map(sent.map((o) => [o.observation_uuid, o]));

  return (
    <section
      aria-label="Recorded"
      className="flex flex-col gap-1 rounded-paper border border-rule bg-wall px-3 py-2"
    >
      <p className="text-sm text-ink-body">
        Recorded · {applied} applied, {duplicate} duplicate · rev{" "}
        <time
          dateTime={receipt.updatedAt}
          title={receipt.updatedAt}
          className="font-mono"
        >
          {clockWord(receipt.updatedAt)}
        </time>
      </p>
      <ul className="flex flex-col gap-0.5 font-mono text-fine-print text-annotation">
        {receipt.results.map((result) => {
          const observation = sentByUuid.get(result.observationUuid);
          return (
            <li key={result.observationUuid} className="flex flex-col">
              <span className="flex flex-wrap items-center gap-2">
                <span>
                  {observation?.op ?? "observation"}{" "}
                  {observation?.slot_id ?? "new slot"} · {result.status}
                </span>
                {leadership && (
                  <TraceToggle
                    eventUuid={result.eventUuid}
                    open={trace === result.eventUuid}
                    onToggle={toggle}
                  />
                )}
              </span>
              <span className="truncate">
                room.observation_recorded {result.eventUuid}
              </span>
            </li>
          );
        })}
        <li className="flex flex-wrap items-center gap-2">
          {receipt.occupantsChangedEventUuid ? (
            <>
              <span className="truncate">
                room.occupants_changed {receipt.occupantsChangedEventUuid}
              </span>
              {leadership && (
                <TraceToggle
                  eventUuid={receipt.occupantsChangedEventUuid}
                  open={trace === receipt.occupantsChangedEventUuid}
                  onToggle={toggle}
                />
              )}
            </>
          ) : (
            <span>Who is in the room did not change.</span>
          )}
        </li>
      </ul>
      {leadership && trace && (
        <TraceDetail
          key={trace}
          eventUuid={trace}
          timezone="UTC"
          active
          onOpenEvent={setTrace}
        />
      )}
      <p className="text-fine-print text-annotation">
        If the camera later disagrees, Brian revises this the normal way.
      </p>
    </section>
  );
};
